<script setup lang="ts">
/**
 * Table cell that displays the 1-based row index for a data row.
 *
 * Rendered in muted color and `tabular-nums` so it reads as secondary
 * metadata; the eye reaches the data cells first. Uses the Tufte serif (not
 * monospace) to match the visual weight of `PositiveIntegerCell` in adjacent
 * columns. Center-aligned.
 */
const props = defineProps<{
  /** 1-based row index (stable across the loaded list). */
  index: number
  /** Max width of the cell (CSS value). Set by DynamicTable from column config. */
  maxWidth?: string
  /** Fixed cell width (CSS value). Set by DynamicTable from column config. */
  width?: string
}>()
</script>

<template>
  <td
    class="dynamic-table__row-index row-index-cell"
    :style="{
      ...(props.maxWidth ? { maxWidth: props.maxWidth, overflowWrap: 'break-word' } : {}),
      ...(props.width ? { width: props.width } : {}),
    }"
  >
    {{ index }}
  </td>
</template>

<style scoped>
/*
 * Row-index cells must match the visual weight of other numeric cells
 * (e.g. `PositiveIntegerCell` used for the Visits column), so we inherit
 * the table's Tufte serif and font-size instead of forcing a monospace
 * face. `tabular-nums` is kept so the digits still line up neatly across
 * rows even at proportional widths.
 */
.row-index-cell {
  text-align: center;
  vertical-align: middle;
  color: var(--color-tufte-muted);
  font-family: var(--font-tufte);
  font-variant-numeric: tabular-nums;
  line-height: var(--line-height-tufte-normal);
}
</style>
